import { Table } from 'antd';
import { Dropdown } from 'antd-v5';
import styled, { css } from 'styled-components';
import { theme } from 'styles';
import { BlueButton, CleverInput, Flex, InputNumber, InputNumberProps, InputPrice, InputPriceProps, Select } from 'UI';

export const STable = styled(Table)`
  thead > tr > th {
    text-align: left;
    white-space: nowrap;
    min-height: 36px;
    height: 36px;
  }

  th.ant-table-cell,
  td.ant-table-cell {
    padding: 2px 4px !important;
  }

  th.ant-table-cell {
    padding: 0 10px !important;
  }

  .ant-input {
    height: 28px;
    padding: 4px;
  }

  .ant-input-group-addon {
    padding: 0 4px;
  }

  .ant-input-affix-wrapper,
  .ant-input-group,
  .ant-input-group-wrapper,
  .ant-input-number,
  .ant-input-number-group-wrapper,
  .ant-select {
    width: 100%;
  }

  .ant-select-selector {
    height: 24px !important;
    padding: 0 7px !important;
  }

  .ant-select-selection-item {
    line-height: 22px !important;
  }

  tbody > tr.ant-table-row {
    :hover {
      box-shadow: 0 1px 4px rgb(0 0 0 / 18%);
    }
  }
` as typeof Table;

const checkEmptyInput = css<{ $isEmpty?: boolean }>`
  border: ${({ theme, $isEmpty }) => ($isEmpty ? `1px solid ${theme.color.red}` : '')};
  background-color: ${({ $isEmpty }) => ($isEmpty ? theme.color.palette_red_1 : '')};
`;

const commonSInvalidInputBorderColor = css<{ $isInvalid?: boolean }>`
  border: ${({ theme, $isInvalid }) => ($isInvalid ? `1px solid ${theme.color.red}` : '')};
`;

const commonSInvalidInputBackgroundColor = css<{ $isInvalid?: boolean }>`
  background-color: ${({ $isInvalid }) => ($isInvalid ? theme.color.palette_red_1 : '')};
`;
const commonSInvalidInputColor = css<{ $isInvalid?: boolean }>`
  color: ${({ theme, $isInvalid }) => ($isInvalid ? theme.color.red : '')};
`;

const commonSInvalidInput = css<{ $isInvalid?: boolean }>`
  ${commonSInvalidInputBorderColor}
  ${commonSInvalidInputBackgroundColor}
  ${commonSInvalidInputColor}
`;

export const commonSInput = css<{ $isInvalid?: boolean }>`
  text-align: center;

  .ant-input,
  .ant-input-group-addon {
    ${commonSInvalidInput}
  }

  .ant-input::placeholder {
    color: ${({ theme, $isInvalid }) => ($isInvalid ? theme.color.red : '')};
  }
`;

export const SInputNumber = styled(InputNumber)<InputNumberProps & { $isInvalid?: boolean }>`
  ${commonSInput}
`;

export const SInputPrice = styled(InputPrice)<InputPriceProps & { $isInvalid?: boolean }>`
  ${commonSInput}
`;

export const SQuantityModuleFlex = styled(Flex)<{ $isInvalid?: boolean }>`
  ${commonSInput}
`;

export const SDiscountInputNumber = styled(InputNumber)<InputNumberProps & { $isInvalid?: boolean }>`
  text-align: center;
  ${commonSInvalidInput}
`;

export const SInput = styled(CleverInput)<{ checkEmpty: boolean; $isEmpty?: boolean; $isInvalid?: boolean }>`
  ${({ readOnly, $isInvalid, $isEmpty }) => {
    if (readOnly && $isInvalid) {
      return css`
        color: ${theme.color.red};
      `;
    }
    if (!readOnly) {
      if ($isInvalid) {
        if ($isEmpty) {
          return css`
            ${commonSInvalidInput}
          `;
        } else {
          return css`
            ${commonSInvalidInputColor}
            :focus {
              border: 1px solid ${({ theme }) => theme.color.brand_light};
              box-shadow: 0 0 0 2px ${({ theme }) => theme.color.brand_background};
              background-color: white;
            }
          `;
        }
      } else {
        return css`
          :focus {
            border: 1px solid ${({ theme }) => theme.color.brand_light};
            box-shadow: 0 0 0 2px ${({ theme }) => theme.color.brand_background};
            background-color: white;
          }
        `;
      }
    } else {
      return css`
        cursor: pointer;
      `;
    }
  }};
  ${({ checkEmpty }) => checkEmpty && checkEmptyInput};

  .ant-input,
  .ant-input-group-addon {
    ${commonSInvalidInput}
`;

export const TooltipContent = styled.div`
  display: flex;
  flex: 1;
`;

export const SSelect = styled(Select)<{ $isInvalid?: boolean }>`
  margin-top: 0 !important;
  margin-bottom: 0 !important;

  ${commonSInvalidInput}
  .ant-select-arrow {
    color: ${({ theme, $isInvalid }) => ($isInvalid ? theme.color.red : '')};
  }
`;

export const SDropdown = styled(Dropdown)`
  ul > li > div {
    padding: 0;
  }
`;

export const ExpandedLaborTimeWrapper = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.color.grey95};
  padding: 5px 55px;
`;

export const ExpandedReferenceWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.color.grey95};
  padding: 20px 15px;
`;

export const ExpandedLaborTimeDotCircleWrapper = styled.div`
  position: relative;
  left: -4px;
  z-index: 1;
`;

export const ExpandedReferenceDotCircleWrapper = styled.div`
  position: relative;
  left: -4.5px;
  top: -13px;
  z-index: 1;
`;

export const LinkedReferencesBackgroundWrapper = styled.div`
  position: absolute;
  display: flex;
  height: 100%;
  left: 45px;
  z-index: 1;
  padding-bottom: 37px;
  top: 25px;
`;

export const CollapsibleContent = styled.div<{ $collapsed: boolean; $margin?: string }>`
  display: grid;
  grid-template-rows: ${({ $collapsed }) => ($collapsed ? '0fr' : '1fr')};
  transition: grid-template-rows 250ms ease;
  overflow: hidden;

  > div {
    overflow-x: auto;
    overflow-y: hidden;
    border-radius: 8px;
    margin: ${({ $margin, $collapsed }) => ($collapsed ? '0' : $margin ?? '0')};
    transition: margin 250ms ease;
    background: white;
  }
`;

export const AddRowButton = styled(BlueButton)`
  background-color: ${theme.color.ice_blue};
  color: ${theme.color.clear_blue};
  border: solid 1px ${theme.color.clear_blue};

  :hover {
    background-color: ${theme.color.info_blue};
    color: ${theme.color.clear_blue};
    border-color: ${theme.color.clear_blue};
  }

  :focus,
  :active {
    background-color: ${theme.color.info_blue};
    color: ${theme.color.clear_blue};
    border-color: ${theme.color.clear_blue};
  }
`;
